import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { ApiUrlConstants } from '../../shared/utils/api-constants';
import { Stamp } from '../../shared/models/stamp';
import { AppHttpParams } from '../../shared/models/app-http-params';

@Injectable({
  providedIn: 'root'
})
export class StampsService {
  constructor(private http: HttpClient) {}

  private mapToFrontendStamp(backendStamp: any): Stamp {
    if (!backendStamp) return backendStamp;
    return {
      ID: backendStamp.id !== undefined ? backendStamp.id : backendStamp.ID,
      Name: backendStamp.name !== undefined ? backendStamp.name : backendStamp.Name,
      Code: backendStamp.code !== undefined ? backendStamp.code : backendStamp.Code,
      Size: backendStamp.stampSize !== undefined ? backendStamp.stampSize : (backendStamp.StampSize !== undefined ? backendStamp.StampSize : (backendStamp.size !== undefined ? backendStamp.size : backendStamp.Size)),
      MinOrderQuantity: backendStamp.minOrderQuantity !== undefined ? backendStamp.minOrderQuantity : backendStamp.MinOrderQuantity,
      Description: backendStamp.description !== undefined ? backendStamp.description : backendStamp.Description,
      ImageUrl: backendStamp.imageUrl !== undefined ? backendStamp.imageUrl : backendStamp.ImageUrl,
      PdfUrl: backendStamp.pdfUrl !== undefined ? backendStamp.pdfUrl : backendStamp.PdfUrl,
      Status: backendStamp.status !== undefined ? backendStamp.status : backendStamp.Status,
      CreationDate: backendStamp.creationDate !== undefined ? backendStamp.creationDate : backendStamp.CreationDate,
      UpdateDate: backendStamp.updateDate !== undefined ? backendStamp.updateDate : backendStamp.UpdateDate,
      Version: backendStamp.version !== undefined ? backendStamp.version : backendStamp.Version,
      ApprovalComment: backendStamp.approvalComment !== undefined ? backendStamp.approvalComment : backendStamp.ApprovalComment,
      ProductDomains: backendStamp.ProductDomains !== undefined ? backendStamp.ProductDomains : backendStamp.ProductDomains || backendStamp.productDomains,
      ProductTypes: backendStamp.ProductTypes !== undefined ? backendStamp.ProductTypes : backendStamp.ProductTypes || backendStamp.productTypes,
      ChangeRequest: backendStamp.ChangeRequest !== undefined ? backendStamp.ChangeRequest : backendStamp.changeRequest,
      PartnerName: backendStamp.PartnerName !== undefined ? backendStamp.PartnerName : backendStamp.PartnerName || backendStamp.partnerName,
      Partner_Name: backendStamp.Partner_Name !== undefined ? backendStamp.Partner_Name : backendStamp.Partner_Name,
      Owner: backendStamp.Owner !== undefined ? backendStamp.Owner : backendStamp.Owner || backendStamp.owner,
      EO_Name: backendStamp.EO_Name !== undefined ? backendStamp.EO_Name : backendStamp.EO_Name,
      EO_ID: backendStamp.EO_ID !== undefined ? backendStamp.EO_ID : backendStamp.EO_ID
    };
  }

  getStamps(params: any): Observable<any> {
    const updatedParams = { ...params };
    if (updatedParams.sizeParam !== undefined) {
      updatedParams.size = updatedParams.sizeParam;
      delete updatedParams.sizeParam;
    }
    const appParams = new AppHttpParams({ params: updatedParams, notifyError: true });
    return this.http.get<any>(AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS, { params: appParams })
      .pipe(
        map(res => {
          if (res) {
            const list = res.Object_List || res.content || [];
            const mappedList = list.map(item => this.mapToFrontendStamp(item));
            if (res.Object_List !== undefined) {
              res.Object_List = mappedList;
            } else if (res.content !== undefined) {
              res.content = mappedList;
            } else {
              res.content = mappedList;
            }
          }
          return res;
        })
      );
  }

  getStamp(id: number): Observable<Stamp> {
    const appParams = new AppHttpParams({ notifyError: true });
    return this.http.get<any>(AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS + ApiUrlConstants.SEPARATOR + id, { params: appParams })
      .pipe(
        map(res => this.mapToFrontendStamp(res))
      );
  }

  getStampImageUrl(id: number): string {
    return AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS + ApiUrlConstants.SEPARATOR + id + '/image';
  }

  getStampSpecificationUrl(id: number): string {
    return AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS + ApiUrlConstants.SEPARATOR + id + '/specification';
  }

  createStamp(formData: FormData): Observable<any> {
    const appParams = new AppHttpParams({ responseLabel: 'stamps.create', notifySuccess: true });
    return this.http.post<any>(AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS, formData, { params: appParams });
  }

  updateStamp(id: number, formData: FormData): Observable<any> {
    const appParams = new AppHttpParams({ responseLabel: 'stamps.update', notifySuccess: true });
    return this.http.put<any>(AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS + ApiUrlConstants.SEPARATOR + id, formData, { params: appParams });
  }

  updateStatus(id: number, status: string, comment: string): Observable<any> {
    const payload: any = {
      status: status
    };
    if (comment) {
      payload.statusChangeComment = comment;
      payload.approvalComment = comment;
      payload.comment = comment;
    }
    const appParams = new AppHttpParams({ responseLabel: 'stamps.updateStatus', notifySuccess: true, notifyError: true });
    return this.http.patch<any>(
      AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS + ApiUrlConstants.SEPARATOR + id + ApiUrlConstants.PRODUCT_STATUS,
      payload,
      { params: appParams }
    );
  }

  deleteStamp(id: number): Observable<any> {
    const appParams = new AppHttpParams({ responseLabel: 'stamps.delete', notifySuccess: true, notifyError: true });
    return this.http.delete<any>(AppConfigService.productMangementUrl + ApiUrlConstants.STAMPS + ApiUrlConstants.SEPARATOR + id, { params: appParams });
  }

  getStampSuggestions(keyword: string): Observable<{ [key: string]: string[] }> {
    if (!keyword?.trim()) {
      return of({});
    }

    return this.http.get<{ [key: string]: string[] }>(
      `${AppConfigService.productMangementUrl}/stamps/suggestions`,
      { params: new HttpParams().set('keyword', keyword).set('limit', '5') }
    ).pipe(
      catchError(() => of({}))
    );
  }
}
